import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { useSubscription } from "@/context/SubscriptionContext";
import { toast } from "sonner";
import { Sparkles, Check } from "lucide-react";
import { BrandMark } from "@/components/BrandMark";

/**
 * Post-checkout landing page. Stripe redirects here with a
 * ``?session_id=…`` query param. We poll ``/subscription/status/{id}``
 * until the webhook (or the polling fallback) confirms payment, then
 * refresh entitlements and offer a return path.
 */
const MAX_POLLS = 10;
const POLL_INTERVAL_MS = 2000;

export default function PricingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const nav = useNavigate();
  const { refresh } = useSubscription();
  const [state, setState] = useState("polling");   // polling | paid | timeout | error
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const { data } = await endpoints.subscriptionStatus(sessionId);
        if (data.payment_status === "paid") {
          await refresh();
          toast.success("Welcome to N8One Pro.");
          setState("paid");
          return;
        }
      } catch { /* transient */ }
      if (attempts.current >= MAX_POLLS) { setState("timeout"); return; }
      setTimeout(tick, POLL_INTERVAL_MS);
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="pricing-success-page">
      <div className="max-w-lg w-full text-center">
        <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--terracotta))] flex items-center justify-center gap-2">
          <Sparkles size={12} />
          <span className="inline-flex items-center gap-1.5"><BrandMark className="text-sm" signOnMount={false} testid="pricing-success-brand" /> Pro</span>
        </div>
        {state === "polling" && (
          <>
            <h1 className="font-display text-3xl mt-3">Confirming your subscription…</h1>
            <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))]">
              Stripe is letting us know. This usually takes a few seconds.
            </p>
          </>
        )}
        {state === "paid" && (
          <>
            <div className="mt-4 inline-flex items-center justify-center w-14 h-14 rounded-full bg-[hsl(var(--terracotta))]/15 text-[hsl(var(--terracotta))]">
              <Check size={22} />
            </div>
            <h1 className="font-display text-4xl mt-4" data-testid="pricing-success-heading">You&rsquo;re in.</h1>
            <p className="mt-3 text-[15px] text-[hsl(var(--fg))]/85 leading-relaxed">
              Welcome to Pro. Your Compass will keep deepening, your Guide is now personalized, and your Monthly Becoming Reflection begins next month.
            </p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <button onClick={() => nav("/becoming?section=discover")} data-testid="pricing-success-becoming" className="rounded-full px-5 py-2.5 bg-[hsl(var(--terracotta))] text-[hsl(var(--paper))] text-sm hover:opacity-90 tab-link">
                Open Becoming
              </button>
              <button onClick={() => nav("/")} className="rounded-full px-5 py-2.5 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))] tab-link">
                Home
              </button>
            </div>
          </>
        )}
        {state === "timeout" && (
          <>
            <h1 className="font-display text-3xl mt-3">Still processing…</h1>
            <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))]">
              Stripe hasn&rsquo;t confirmed yet. It&rsquo;s safe to close this and check back — we&rsquo;ll activate Pro as soon as payment is confirmed.
            </p>
            <Link to="/" className="mt-6 inline-block text-sm underline">Back to Home</Link>
          </>
        )}
        {state === "error" && (
          <>
            <h1 className="font-display text-3xl mt-3">Missing session</h1>
            <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))]">We couldn&rsquo;t find that checkout session.</p>
            <Link to="/" className="mt-6 inline-block text-sm underline">Back to Home</Link>
          </>
        )}
      </div>
    </div>
  );
}
